/**
 * 🔥 VIRAL - Features pour faire buzzer l'app
 * 
 * Contient :
 * - Hot or Not - Classement popularité du quartier
 * - Share Card - Carte partageableInstagram/TikTok
 * - Crush Anonyme - Notifié que si réciproque
 * - Invite Friends - Parrainage
 * 
 * Objectif : Faire parler de l'app !
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Flame, Trophy, Share2, Heart, Users, 
  Crown, Star, Sparkles, Gift, Copy,
  Instagram, Twitter, MessageCircle, Lock,
  Eye, EyeOff, Send, Check, X
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

// 🔥 HOT OR NOT - Classement du quartier
export function HotOrNot({ profiles = [], currentUserId, onVote, isDarkMode }) {
  const [currentPair, setCurrentPair] = useState([]);
  const [votes, setVotes] = useState({});
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  
  // Générer une paire aléatoire
  useEffect(() => {
    if (profiles.length >= 2) {
      const shuffled = [...profiles].filter(p => p.id !== currentUserId).sort(() => Math.random() - 0.5);
      setCurrentPair([shuffled[0], shuffled[1]]);
    }
  }, [profiles, currentUserId]);
  
  const handleVote = (winnerId) => {
    setVotes(prev => ({
      ...prev,
      [winnerId]: (prev[winnerId] || 0) + 1
    }));
    
    onVote && onVote(winnerId);
    
    // Nouvelle paire
    const shuffled = [...profiles].filter(p => p.id !== currentUserId).sort(() => Math.random() - 0.5);
    setCurrentPair([shuffled[0], shuffled[1]]);
    
    toast.success('🔥 Vote enregistré !');
  };
  
  // Calculer le leaderboard
  const leaderboard = profiles
    .map(p => ({ ...p, score: votes[p.id] || Math.floor(Math.random() * 50) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);
  
  if (showLeaderboard) {
    return (
      <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-500" />
            <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              🔥 Top 10 du quartier
            </h3>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowLeaderboard(false)}>
            Voter
          </Button>
        </div>
        
        <div className="space-y-3">
          {leaderboard.map((profile, idx) => (
            <motion.div
              key={profile.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`flex items-center gap-3 p-3 rounded-xl ${
                idx === 0 ? 'bg-gradient-to-r from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30' :
                idx === 1 ? 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700/50 dark:to-gray-600/50' :
                idx === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20' :
                isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                idx === 0 ? 'bg-yellow-500 text-white' :
                idx === 1 ? 'bg-gray-400 text-white' :
                idx === 2 ? 'bg-orange-400 text-white' :
                'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
              }`}>
                {idx + 1}
              </div>
              
              {/* Avatar */}
              <div className="w-12 h-12 rounded-full overflow-hidden">
                {profile.photos?.[0] ? (
                  <img src={profile.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white font-bold">
                    {profile.name?.[0] || '?'}
                  </div>
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1">
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {profile.name}, {profile.age}
                </p>
                <div className="flex items-center gap-1">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className={`text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    {profile.score} votes
                  </span>
                </div>
              </div>
              
              {/* Crown pour le #1 */}
              {idx === 0 && <Crown className="w-6 h-6 text-yellow-500" />}
            </motion.div>
          ))}
        </div>
      </div>
    );
  }
  
  if (currentPair.length < 2) {
    return (
      <div className={`rounded-2xl p-8 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
        <Flame className="w-12 h-12 mx-auto mb-4 text-orange-500" />
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Hot or Not
        </h3>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
          Pas assez de profils pour jouer. Reviens plus tard !
        </p>
      </div>
    );
  }
  
  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Flame className="w-6 h-6 text-orange-500" />
          <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            🔥 Hot or Not
          </h3>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setShowLeaderboard(true)}>
          <Trophy className="w-4 h-4 mr-1" />
          Top 10
        </Button>
      </div>
      
      <p className={`text-center mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Qui est le/la plus hot du quartier ? 🔥
      </p>
      
      {/* VS Cards */}
      <div className="grid grid-cols-2 gap-4">
        {currentPair.map((profile, idx) => (
          <motion.button
            key={profile.id}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => handleVote(profile.id)}
            className="relative rounded-2xl overflow-hidden aspect-[3/4] group"
          >
            {profile.photos?.[0] ? (
              <img 
                src={profile.photos[0]} 
                alt="" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-4xl font-bold">
                {profile.name?.[0] || '?'}
              </div>
            )}
            
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            
            {/* Info */}
            <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
              <p className="font-bold">{profile.name}, {profile.age}</p>
            </div>
            
            {/* Hover effect */}
            <div className="absolute inset-0 bg-pink-500/0 group-hover:bg-pink-500/30 transition-colors flex items-center justify-center">
              <motion.div
                initial={{ scale: 0 }}
                whileHover={{ scale: 1 }}
                className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center"
              >
                <Flame className="w-8 h-8 text-orange-500" />
              </motion.div>
            </div>
          </motion.button>
        ))}
      </div>
      
      {/* VS badge */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center font-black text-orange-500">
          VS
        </div>
      </div>
    </div>
  );
}

// 💘 CRUSH ANONYME - Notifié que si réciproque
// Deux modes : 
// 1. targetProfile = affiche le formulaire pour envoyer un crush
// 2. customerId + crushes = affiche la liste des crushes
export function SecretCrush({ 
  targetProfile, 
  onSendCrush, 
  onClose, 
  isDarkMode,
  // Props pour le mode "liste des crushes"
  customerId,
  crushes = [],
  onCrushSent
}) {
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  
  // MODE LISTE : Afficher les crushes existants
  if (customerId && !targetProfile) {
    return (
      <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">💘</span>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Mes Crushes Secrets
          </h3>
        </div>
        
        {crushes.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-4xl mb-3">💝</div>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Tu n'as pas encore envoyé de crush secret
            </p>
            <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Parcours les profils et clique sur 💘 pour envoyer un crush anonyme !
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {crushes.map((crush, idx) => (
              <motion.div
                key={crush.id || idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`p-3 rounded-xl flex items-center gap-3 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-pink-50'
                }`}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white">
                  {crush.targetPhoto ? (
                    <img src={crush.targetPhoto} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span>{crush.targetName?.[0] || '?'}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {crush.targetName || 'Anonyme'}
                  </p>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {crush.isMatched ? '💕 Match réciproque !' : '⏳ En attente...'}
                  </p>
                </div>
                {crush.isMatched && (
                  <span className="text-green-500 text-xl">💚</span>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // MODE FORMULAIRE : Envoyer un crush à un profil spécifique
  // Protection si targetProfile est undefined
  if (!targetProfile) {
    return (
      <div className={`rounded-2xl p-6 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
          Sélectionne un profil pour envoyer un crush secret 💘
        </p>
        {onClose && (
          <Button className="mt-4" variant="outline" onClick={onClose}>
            Fermer
          </Button>
        )}
      </div>
    );
  }
  
  const handleSend = () => {
    onSendCrush && onSendCrush({
      targetId: targetProfile.id,
      message,
      anonymous: true,
      timestamp: new Date().toISOString()
    });
    
    setSent(true);
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.7 },
      colors: ['#EC4899', '#8B5CF6']
    });
  };
  
  if (sent) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`rounded-2xl p-6 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}
      >
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.5 }}
          className="text-6xl mb-4"
        >
          💘
        </motion.div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Crush envoyé ! ✨
        </h3>
        <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {targetProfile.name} sera notifié(e) <strong>uniquement</strong> si il/elle a aussi un crush sur toi !
        </p>
        <p className={`text-sm ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
          🤞 Croisons les doigts...
        </p>
        <Button className="mt-4" variant="outline" onClick={onClose}>
          Fermer
        </Button>
      </motion.div>
    );
  }
  
  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl">💘</span>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Crush Anonyme
          </h3>
        </div>
        <button onClick={onClose}>
          <X className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        </button>
      </div>
      
      {/* Target profile */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-14 h-14 rounded-full overflow-hidden">
          {targetProfile.photos?.[0] ? (
            <img src={targetProfile.photos[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
              {targetProfile.name?.[0] || '?'}
            </div>
          )}
        </div>
        <div>
          <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {targetProfile.name}
          </p>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {targetProfile.age} ans
          </p>
        </div>
      </div>
      
      {/* Explication */}
      <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-pink-900/30' : 'bg-pink-50'}`}>
        <div className="flex items-start gap-2">
          <Lock className={`w-5 h-5 mt-0.5 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} />
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-pink-300' : 'text-pink-700'}`}>
              100% discret !
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-pink-200' : 'text-pink-600'}`}>
              {targetProfile.name} ne saura que tu as un crush sur lui/elle QUE si c'est réciproque.
            </p>
          </div>
        </div>
      </div>
      
      {/* Message optionnel */}
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Un petit mot ? (optionnel, sera révélé si match)"
        maxLength={100}
        rows={2}
        className={`w-full p-3 rounded-xl mb-4 resize-none ${
          isDarkMode
            ? 'bg-gray-700 text-white border-gray-600'
            : 'bg-gray-100 text-gray-900 border-gray-200'
        } border focus:ring-2 focus:ring-pink-500`}
      />
      
      <Button
        className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white"
        onClick={handleSend}
      >
        <Heart className="w-5 h-5 mr-2" />
        Envoyer mon crush secret 💘
      </Button>
    </div>
  );
}

// 📱 SHARE CARD - Carte partageable
export function ShareCard({ profile, stats, onClose, isDarkMode }) {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = `https://heartbeats.fr/p/${profile?.id || 'demo'}`;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShare = (platform) => {
    const text = `💜 Découvre mon profil sur Heartbeats - L'app de rencontres qui fait battre les cœurs plus fort ! ✨`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`,
      instagram: shareUrl // Instagram ne supporte pas le partage direct
    };
    
    if (platform === 'instagram') {
      handleCopy();
      toast.info('Lien copié ! Colle-le dans ta story Instagram 📸');
    } else {
      window.open(urls[platform], '_blank');
    }
  };
  
  return (
    <div className={`rounded-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      {/* Preview Card */}
      <div 
        className="relative p-6 text-center"
        style={{
          background: 'linear-gradient(135deg, #EC4899 0%, #8B5CF6 50%, #3B82F6 100%)'
        }}
      >
        {/* Avatar */}
        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-white shadow-xl mb-4">
          {profile?.photos?.[0] ? (
            <img src={profile.photos[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-white/20 flex items-center justify-center text-white text-3xl font-bold">
              {profile?.name?.[0] || '?'}
            </div>
          )}
        </div>
        
        <h3 className="text-white text-xl font-bold mb-1">
          {profile?.name}, {profile?.age}
        </h3>
        
        {profile?.tagline && (
          <p className="text-white/80 text-sm italic mb-4">
            "{profile.tagline}"
          </p>
        )}
        
        {/* Stats */}
        <div className="flex justify-center gap-6 text-white">
          <div>
            <p className="text-2xl font-bold">{stats?.matches || 0}</p>
            <p className="text-xs opacity-70">Matchs</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{stats?.likes || 0}</p>
            <p className="text-xs opacity-70">Likes</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{stats?.views || 0}</p>
            <p className="text-xs opacity-70">Vues</p>
          </div>
        </div>
        
        {/* Watermark */}
        <div className="absolute bottom-2 right-2 opacity-50">
          <span className="text-white text-xs font-medium">💜 Heartbeats</span>
        </div>
      </div>
      
      {/* Actions */}
      <div className="p-4">
        <p className={`text-center mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          Partage ton profil et fais-toi connaître ! 🚀
        </p>
        
        {/* Share buttons */}
        <div className="flex justify-center gap-3 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShare('instagram')}
            className="rounded-full"
          >
            <Instagram className="w-5 h-5 text-pink-500" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShare('twitter')}
            className="rounded-full"
          >
            <Twitter className="w-5 h-5 text-blue-400" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleShare('whatsapp')}
            className="rounded-full"
          >
            <MessageCircle className="w-5 h-5 text-green-500" />
          </Button>
        </div>
        
        {/* Copy link */}
        <Button
          variant="outline"
          className="w-full"
          onClick={handleCopy}
        >
          {copied ? (
            <><Check className="w-4 h-4 mr-2 text-green-500" /> Copié !</>
          ) : (
            <><Copy className="w-4 h-4 mr-2" /> Copier le lien</>
          )}
        </Button>
      </div>
    </div>
  );
}

// 👥 INVITE FRIENDS - Parrainage MODERNE avec LIENS
export function InviteFriends({ referralCode, referralCount = 0, onShare, isDarkMode }) {
  const [copied, setCopied] = useState(false);
  
  // 🔗 Lien d'affiliation moderne avec tracking
  const inviteLink = `https://heartbeats.fr/?ref=${referralCode}`;
  
  const handleCopy = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success('🔗 Lien copié !');
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleShareWhatsApp = () => {
    const text = `🔥 Rejoins Heartbeats, l'app de rencontre la plus cool ! Utilise mon lien pour 15 jours GRATUITS 👇\n${inviteLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };
  
  const handleShareSMS = () => {
    const text = `Rejoins Heartbeats avec mon lien : ${inviteLink}`;
    window.open(`sms:?body=${encodeURIComponent(text)}`, '_blank');
  };
  
  const handleShareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Rejoins Heartbeats !',
          text: '🔥 Utilise mon lien pour 15 jours GRATUITS sur Heartbeats Premium !',
          url: inviteLink
        });
      } catch (err) {
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };
  
  const rewards = [
    { count: 1, reward: '10 HBT', emoji: '💎' },
    { count: 3, reward: 'Badge "Influenceur"', emoji: '⭐' },
    { count: 5, reward: '1 Super Like', emoji: '💜' },
    { count: 10, reward: '1 mois Premium', emoji: '👑' }
  ];
  
  return (
    <div className={`rounded-2xl p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-xl`}>
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-3xl mb-4">
          🔗
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Partage ton lien !
        </h3>
        <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
          Gagne des récompenses pour chaque ami inscrit
        </p>
      </div>
      
      {/* Referral stats */}
      <div className={`p-4 rounded-xl mb-6 text-center ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
        <p className={`text-3xl font-bold ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
          {referralCount}
        </p>
        <p className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
          ami(s) inscrit(s) via ton lien
        </p>
      </div>
      
      {/* 🔗 LIEN D'AFFILIATION MODERNE */}
      <div className={`p-4 rounded-xl mb-4 border-2 border-dashed ${isDarkMode ? 'border-pink-500/50 bg-pink-900/20' : 'border-pink-300 bg-pink-50'}`}>
        <p className={`text-xs mb-2 uppercase tracking-wide font-semibold ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
          🔗 Ton lien d'affiliation
        </p>
        <p className={`text-sm font-mono break-all ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {inviteLink}
        </p>
        <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Tracking automatique • Cookie 90 jours
        </p>
      </div>
      
      {/* Boutons de partage */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <Button
          onClick={handleCopy}
          className={`flex-col py-3 h-auto ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          variant="ghost"
        >
          {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
          <span className="text-xs mt-1">{copied ? 'Copié!' : 'Copier'}</span>
        </Button>
        
        <Button
          onClick={handleShareWhatsApp}
          className="flex-col py-3 h-auto bg-green-500 hover:bg-green-600 text-white"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="text-xs mt-1">WhatsApp</span>
        </Button>
        
        <Button
          onClick={handleShareNative}
          className="flex-col py-3 h-auto bg-gradient-to-r from-pink-500 to-purple-500 text-white"
        >
          <Share2 className="w-5 h-5" />
          <span className="text-xs mt-1">Partager</span>
        </Button>
      </div>
      
      {/* Rewards */}
      <div className="space-y-2">
        <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Récompenses
        </p>
        {rewards.map((r) => (
          <div
            key={r.count}
            className={`flex items-center justify-between p-3 rounded-lg ${
              referralCount >= r.count
                ? isDarkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200'
                : isDarkMode ? 'bg-gray-700/50' : 'bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-xl">{r.emoji}</span>
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                {r.count} ami{r.count > 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {r.reward}
              </span>
              {referralCount >= r.count && (
                <Check className="w-5 h-5 text-green-500" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default HotOrNot;
